import type { ComponentPropsWithRef, ComponentType, SVGProps } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms/icon';
import { ProgressBar, type ProgressBarProps } from '@/shared-ui/atoms/progress-bar';
import { Typography } from '@/shared-ui/atoms/typography';
import { Tooltip } from '@/shared-ui/molecules/tooltip';

// Root -----------------------------------------------------------------------

type RootProps = ComponentPropsWithRef<'div'>;

const Root = ({ className, ref, ...props }: RootProps) => (
    <div
        data-slot="cart-message"
        ref={ref}
        className={cn('flex flex-col gap-1 bg-background border border-snk-greige p-2', className)}
        {...props}
    />
);
Root.displayName = 'CartMessage.Root';

// Body -----------------------------------------------------------------------

type BodyProps = ComponentPropsWithRef<'div'>;

const Body = ({ className, ref, ...props }: BodyProps) => (
    <div data-slot="cart-message-body" ref={ref} className={cn('flex items-center gap-2', className)} {...props} />
);
Body.displayName = 'CartMessage.Body';

// Copy -----------------------------------------------------------------------

type CopyProps = ComponentPropsWithRef<'p'>;

const Copy = ({ className, ref, children, ...props }: CopyProps) => (
    <Typography
        data-slot="cart-message-copy"
        variant="caption"
        as="p"
        ref={ref as never}
        className={cn('grow', className)}
        {...props}>
        {children}
    </Typography>
);
Copy.displayName = 'CartMessage.Copy';

// Icon -----------------------------------------------------------------------

type MessageIconProps = Omit<ComponentPropsWithRef<'button'>, 'children' | 'type' | 'aria-label'> & {
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    iconClassName?: string;
    /** Accessible label for the button; also wired to Tooltip content when the Tooltip molecule is available */
    tooltip: string;
};

const MessageIcon = ({ icon, iconClassName, tooltip, className, ref, ...props }: MessageIconProps) => (
    <Tooltip.Root>
        <Tooltip.Trigger asChild>
            <button
                {...props}
                type="button"
                aria-label={tooltip}
                data-slot="cart-message-icon"
                ref={ref}
                className={cn('ml-auto shrink-0', className)}>
                <Icon icon={icon} decorative className={cn('size-4', iconClassName)} />
            </button>
        </Tooltip.Trigger>
        <Tooltip.Content>
            <Tooltip.Body>{tooltip}</Tooltip.Body>
        </Tooltip.Content>
    </Tooltip.Root>
);
MessageIcon.displayName = 'CartMessage.Icon';

// ProgressBar ----------------------------------------------------------------

type MessageProgressBarProps = ProgressBarProps;

const MessageProgressBar = ({ className, ...props }: MessageProgressBarProps) => (
    <ProgressBar data-slot="cart-message-progress-bar" className={cn('h-[6px]', className)} {...props} />
);
MessageProgressBar.displayName = 'CartMessage.ProgressBar';

// Namespace export -----------------------------------------------------------

const CartMessage = {
    Root,
    Body,
    Copy,
    Icon: MessageIcon,
    ProgressBar: MessageProgressBar,
} as const;

export { CartMessage };
